import * as THREE from 'three';
import { isSketchGroup, type SketchGroup } from '../../../mesh/sketch';
import type { ReferencePlaneHostFaceDescriptor } from '../../../reference/ReferencePlaneHostFace';
import type { ExtrudeFeatureEditDefinition } from './ExtrudeFeatureEditDefinition';
import type { ExtrudeSessionTarget } from './ExtrudeFeatureSessionBridge';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/edit/extrude 层；
 * 2. 负责解析拉伸族特征当前应优先命中的宿主目标体；
 * 3. 该解析结果会被切除 Feature Scope、默认预览种子和后续编辑恢复共同复用。
 *
 * 文件职责：
 * 1. 统一处理“草图宿主面 -> 宿主实体体”的推导；
 * 2. 优先消费显式持久化的 hostedContourInput，避免每条链路各自猜测；
 * 3. 解析结果仅用于草图平面追溯与预览深度提示，不再锁定切除目标体。
 */

/** 拉伸宿主解析结果。 */
export interface ExtrudeHostedTargetResolution {
    /** 预览深度提示使用的宿主体 ID，不表示最终切除目标体。 */
    targetBodyId: string | null;
    /** 草图宿主面所属对象 ID。 */
    supportFaceObjectId: string | null;
    /** 草图宿主面索引。 */
    supportFaceFaceIndex: number | null;
}

/** 拉伸宿主目标体解析器。 */
export class ExtrudeHostedTargetBodyResolver {
    /**
     * 解析当前拉伸目标对应的完整宿主描述。
     *
     * @param input 解析输入。
     * @returns 宿主体与宿主面的稳定描述。
     */
    public resolve(input: {
        scene: THREE.Scene | null;
        target: ExtrudeSessionTarget;
        definition: ExtrudeFeatureEditDefinition;
    }): ExtrudeHostedTargetResolution {
        const persistedHost = input.definition.cutOptions?.hostedContourInput;
        const runtimeHostFace = this.resolveRuntimeHostFace(input.scene, input.target);
        const directFaceHost = this.resolveDirectFaceHost(input.target);

        const supportFaceObjectId = this.normalizeObjectId(persistedHost?.supportFaceObjectId)
            ?? runtimeHostFace?.objectId
            ?? directFaceHost?.objectId
            ?? null;
        const supportFaceFaceIndex = Number.isInteger(persistedHost?.supportFaceFaceIndex)
            ? persistedHost!.supportFaceFaceIndex!
            : runtimeHostFace?.faceIndex
                ?? directFaceHost?.faceIndex
                ?? null;
        const targetBodyId = this.normalizeObjectId(persistedHost?.targetBodyId)
            ?? this.resolveDirectTargetBodyId(input.target)
            ?? runtimeHostFace?.objectId
            ?? supportFaceObjectId;

        return {
            targetBodyId,
            supportFaceObjectId,
            supportFaceFaceIndex,
        };
    }

    /**
     * 从运行时草图参考平面读取宿主面。
     *
     * @param scene 当前场景。
     * @param target 当前拉伸目标。
     * @returns 宿主面描述；非草图轮廓目标时返回 null。
     */
    private resolveRuntimeHostFace(
        scene: THREE.Scene | null,
        target: ExtrudeSessionTarget,
    ): ReferencePlaneHostFaceDescriptor | null {
        const sketchId = target.regions?.[0]?.sketchId ?? null;
        if (!sketchId) {
            return null;
        }

        const runtimeSketchGroup = scene?.getObjectByProperty('uuid', sketchId) ?? null;
        if (!runtimeSketchGroup || !isSketchGroup(runtimeSketchGroup)) {
            return null;
        }
        return (runtimeSketchGroup as SketchGroup).referencePlane.hostFace;
    }

    /**
     * 从直接面目标读取宿主面描述。
     *
     * @param target 当前拉伸目标。
     * @returns 可解析的面宿主描述。
     */
    private resolveDirectFaceHost(
        target: ExtrudeSessionTarget,
    ): ReferencePlaneHostFaceDescriptor | null {
        if (target.params.sourceType !== 'solid_face') {
            return null;
        }
        const objectId = this.normalizeObjectId(target.params.solidId);
        const faceIndex = target.params.faceIndex;
        if (!objectId || !Number.isInteger(faceIndex)) {
            return null;
        }
        return {
            objectId,
            faceIndex: faceIndex!,
        };
    }

    /**
     * 从直接几何目标解析预览宿主体 ID。
     *
     * @param target 当前拉伸目标。
     * @returns 宿主体 ID；轮廓目标返回 null。
     */
    private resolveDirectTargetBodyId(target: ExtrudeSessionTarget): string | null {
        if (target.params.sourceType === 'face') {
            return this.normalizeObjectId(target.params.faceId);
        }
        if (target.params.sourceType === 'solid_face') {
            return this.normalizeObjectId(target.params.solidId);
        }
        return null;
    }

    /**
     * 规范化对象 ID。
     *
     * @param value 原始值。
     * @returns 非空 ID；无效时返回 null。
     */
    private normalizeObjectId(value: unknown): string | null {
        return typeof value === 'string' && value.trim() ? value.trim() : null;
    }
}
